import React, { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { AppBar, Box, Hidden, IconButton, Link, ListItemIcon, ListItemText, Menu as MUIMenu, MenuItem, Paper, Toolbar, Typography } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import { Apps, AttachMoney, Menu as MenuIcon, VpnKey } from '@mui/icons-material';
import { bindMenu, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';

import ImageFluid from '@components/common/ImageFluid';
import { useConfig } from '@utils/config';
import HeroCTAButton from './HeroCTAButton';

function Menu() {
  const { t } = useTranslation();
  const Config = useConfig();
  const theme = useTheme();

  const mobileMenuState = usePopupState({ variant: 'popover', popupId: 'mobileMenu' });

  const DesktopMenuItem = ({ to, children }) => (
    <Box px={2} sx={{ color: theme.maligue.grayBackgroundColor }} display="flex" alignItems="center">
      <Typography variant="button">
        <Link component={RouterLink} to={to} color="inherit" underline="none">
          {children}
        </Link>
      </Typography>
    </Box>
  );

  // Need to use forwardRef because this is used in a MUIMenu
  const MobileMenuItem = React.forwardRef<HTMLLIElement, PropsWithChildren<{ icon?: React.ReactNode; to: string }>>((props, ref) => {
    return (
      <Link component={RouterLink} to={props.to} color="inherit" underline="none" onClick={mobileMenuState.close}>
        <MenuItem ref={ref} {...props}>
          {props.icon && <ListItemIcon>{props.icon}</ListItemIcon>}
          <ListItemText>{props.children}</ListItemText>
        </MenuItem>
      </Link>
    );
  });

  const menuItems = [
    { to: '/features', content: t('menu.features'), mobileIcon: <Apps /> },
    { to: '/pricing', content: t('menu.pricing'), mobileIcon: <AttachMoney /> },
    { to: '/signin', content: t('menu.signin'), mobileIcon: <VpnKey /> },
    { to: '/signup', content: <HeroCTAButton>{t('menu.CTA')}</HeroCTAButton> },
  ];

  return (
    <>
      <AppBar position="fixed" color="transparent" elevation={0}>
        <Toolbar>
          {/* Desktop*/}
          <Hidden smDown>
            <Box display="flex" height={theme.menu.desktop.height} overflow="visible">
              <Link component={RouterLink} to="/" underline="hover">
                <Box p={2}>
                  <ImageFluid height="80px" alt="logo" src={`/images/logo/${Config.appBranding}/small_fill_white.png`} />
                </Box>
              </Link>
            </Box>
            <Box display="flex" flexGrow={1} />
            <Box
              display="flex"
              sx={{
                height: (theme: Theme) => theme.menu.desktop.height,
                paddingLeft: '50px',
                paddingRight: '30px',
                backgroundColor: 'rgba(0, 0, 0, .45)',
                borderRadius: '0 0 1.4rem 1.4rem',
                borderTopWidth: '0px',
                boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
              }}
            >
              {menuItems.map((menuItem) => (
                <DesktopMenuItem key={menuItem.to} to={menuItem.to}>
                  {menuItem.content}
                </DesktopMenuItem>
              ))}
            </Box>
          </Hidden>

          {/* Mobile */}
          <Hidden smUp>
            <Box display="flex" flexGrow={1} alignItems="flex-start" pt={2}>
              <Link component={RouterLink} to="/" underline="hover">
                <Box display="flex" justifyContent="center" flexBasis="20%" height={theme.menu.mobile.height} overflow="visible">
                  <ImageFluid height="60px" alt="logo" src={`/images/logo/${Config.appBranding}/small_fill_white.png`} />
                </Box>
              </Link>
              <Box display="flex" justifyContent="center" flexBasis="60%" />
              <Box display="flex" justifyContent="center" flexBasis="20%">
                <Paper>
                  <IconButton color="inherit" {...bindTrigger(mobileMenuState)} size="large">
                    <MenuIcon />
                  </IconButton>
                </Paper>
              </Box>
            </Box>
          </Hidden>
        </Toolbar>
      </AppBar>
      <MUIMenu {...bindMenu(mobileMenuState)} anchorOrigin={{ vertical: 'top', horizontal: 'right' }} keepMounted transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        {menuItems.map((menuItem) => (
          <MobileMenuItem key={menuItem.to} to={menuItem.to} icon={menuItem.mobileIcon}>
            {menuItem.content}
          </MobileMenuItem>
        ))}
      </MUIMenu>
    </>
  );
}

export default Menu;
